import { useState } from 'react';

import { apiErrorMessage } from '@/api/errors';
import {
  hasKeyProblem,
  integrationsApi,
  type Integration,
  type IntegrationTestResult,
  type IntegrationType,
} from '@/api/queries/integrations';
import {
  Button,
  Field,
  Icon,
  IconButton,
  Input,
  ModalRoot,
  StatusPill,
  useToast,
  type IconName,
} from '@/components/ui';
import { timeAgo } from '@/utils/formatDate';

import { validateApiKey } from './integrationKeyRules';

interface IntegrationKindLite {
  type: IntegrationType;
  name: string;
  tagline: string;
  icon: IconName;
  accent: string;
  apiKeysUrl: string;
  apiKeysHint: string;
  apiKeyPlaceholder: string;
  apiEndpoint: string;
  supportsSender?: boolean;
  supportsBaseUrl?: boolean;
}

interface IntegrationCardModalProps {
  kind: IntegrationKindLite;
  accounts: Integration[];
  open: boolean;
  onClose: () => void;
}

/**
 * IntegrationCardModal — opened when the user clicks an integration card on
 * the Settings page. Lists already-configured accounts (each with edit and
 * remove actions) and an inline form to connect or edit one. All
 * provider-specific copy and capabilities (API-key location, placeholder,
 * base URL, sender fields) come from the `kind`.
 */
export function IntegrationCardModal({
  kind,
  accounts,
  open,
  onClose,
}: IntegrationCardModalProps) {
  const create = integrationsApi.useCreate();
  const update = integrationsApi.useUpdate();
  const remove = integrationsApi.useDelete();
  const test = integrationsApi.useTest();
  const testStored = integrationsApi.useTestStored();
  const toast = useToast();

  const defaultBaseUrl = kind.supportsBaseUrl ? kind.apiEndpoint : '';

  const [name, setName] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [fromName, setFromName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [baseUrl, setBaseUrl] = useState(defaultBaseUrl);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<IntegrationTestResult | null>(null);
  const [adding, setAdding] = useState(accounts.length === 0);
  // The account currently being edited (null = creating a new one).
  const [editing, setEditing] = useState<Integration | null>(null);

  const formOpen = adding || editing !== null;
  const busy = create.isPending || update.isPending;
  const testing = test.isPending || testStored.isPending;
  const keyError = validateApiKey(kind.type, apiKey);
  const canTest = !keyError && (apiKey.trim() !== '' || editing !== null);

  const clearFields = () => {
    setName('');
    setApiKey('');
    setFromName('');
    setFromEmail('');
    setReplyTo('');
    setBaseUrl(defaultBaseUrl);
    setError(null);
    setTestResult(null);
  };

  const close = () => {
    clearFields();
    setEditing(null);
    setAdding(accounts.length === 0);
    onClose();
  };

  // Dismiss the inline form but stay in the modal when there are accounts to
  // return to; otherwise close the whole modal.
  const backToList = () => {
    clearFields();
    setEditing(null);
    setAdding(false);
  };

  const beginAdd = () => {
    clearFields();
    setEditing(null);
    setAdding(true);
  };

  const beginEdit = (a: Integration) => {
    setName(a.name ?? '');
    setApiKey('');
    setFromName(a.fromName ?? '');
    setFromEmail(a.fromEmail ?? '');
    setReplyTo(a.replyTo ?? '');
    setBaseUrl(a.baseUrl ?? defaultBaseUrl);
    setError(null);
    setTestResult(null);
    setAdding(false);
    setEditing(a);
  };

  const cancel = () => {
    if (formOpen && accounts.length > 0) backToList();
    else close();
  };

  const submit = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    const isEditing = editing !== null;
    if (!isEditing && !apiKey.trim()) {
      setError('API key is required');
      return;
    }
    if (keyError) {
      setError(keyError);
      return;
    }
    if (kind.supportsSender) {
      if (!fromName.trim()) {
        setError('Sender name is required');
        return;
      }
      if (!fromEmail.trim()) {
        setError('Sender email is required');
        return;
      }
    }
    try {
      if (isEditing && editing) {
        await update.mutateAsync({
          id: editing.id,
          body: {
            name: name.trim(),
            ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
            ...(kind.supportsSender
              ? { fromName: fromName.trim(), fromEmail: fromEmail.trim(), replyTo: replyTo.trim() }
              : {}),
            ...(kind.supportsBaseUrl ? { baseUrl: baseUrl.trim() } : {}),
          },
        });
        toast.show({
          severity: 'success',
          title: `${kind.name} updated`,
          description: `"${name.trim()}" has been updated.`,
        });
      } else {
        await create.mutateAsync({
          type: kind.type,
          name: name.trim(),
          apiKey: apiKey.trim(),
          ...(kind.supportsSender
            ? {
                fromName: fromName.trim(),
                fromEmail: fromEmail.trim(),
                replyTo: replyTo.trim() || undefined,
              }
            : {}),
          ...(kind.supportsBaseUrl ? { baseUrl: baseUrl.trim() || undefined } : {}),
        });
        toast.show({
          severity: 'success',
          title: `${kind.name} connected`,
          description: `"${name.trim()}" is ready to use from the Connect to dialog.`,
        });
      }
      backToList();
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to save integration'));
    }
  };

  const runTest = async () => {
    setTestResult(null);
    const key = apiKey.trim();
    try {
      if (key) {
        if (keyError) return;
        const trimmedBaseUrl = baseUrl.trim();
        setTestResult(
          await test.mutateAsync({
            type: kind.type,
            apiKey: key,
            ...(kind.supportsBaseUrl && trimmedBaseUrl ? { baseUrl: trimmedBaseUrl } : {}),
          }),
        );
      } else if (editing) {
        setTestResult(await testStored.mutateAsync(editing.id));
      }
    } catch (e) {
      setTestResult({ ok: false, message: apiErrorMessage(e, 'Connection test failed.') });
    }
  };

  const removeAccount = (a: Integration) => {
    if (
      !confirm(
        `Remove "${a.name}"? Segments synced through this connection will lose their link to the external list.`,
      )
    ) {
      return;
    }
    remove.mutate(a.id, {
      onSuccess: () =>
        toast.show({
          severity: 'info',
          title: 'Integration removed',
          description: `"${a.name}" has been disconnected.`,
        }),
      onError: (e) =>
        toast.show({
          severity: 'error',
          title: 'Could not remove integration',
          description: apiErrorMessage(e, `"${a.name}" could not be removed.`),
        }),
    });
  };

  return (
    <ModalRoot open={open} onClose={close} size="md" closeOnScrim>
      <div className="ds-modal-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-sm)',
              background: kind.accent,
              color: '#fff',
              display: 'inline-grid',
              placeItems: 'center',
            }}
          >
            <Icon name={kind.icon} size={16} />
          </div>
          <h2 style={{ margin: 0 }}>{kind.name}</h2>
        </div>
        <IconButton icon="x" label="Close" className="ds-modal-close" onClick={close} />
      </div>
      <div className="ds-modal-body">
        <div className="ds-form-row">
          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{kind.tagline}</div>

          {accounts.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--text-3)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Connected accounts
              </div>
              {accounts.map((a) => (
                <div
                  key={a.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    background: 'var(--surface-2, #f6f7f9)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    outline: editing?.id === a.id ? '2px solid var(--brand)' : 'none',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{a.name}</div>
                    <div
                      style={{
                        fontSize: 11,
                        fontFamily: 'var(--font-mono)',
                        color: 'var(--text-3)',
                      }}
                    >
                      {a.apiKeyMask}
                    </div>
                    {a.fromEmail && (
                      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                        Sender: {a.fromName ? `${a.fromName} <${a.fromEmail}>` : a.fromEmail}
                      </div>
                    )}
                    {hasKeyProblem(a) && a.keyStatusMessage && (
                      <div style={{ fontSize: 11, color: 'var(--red)' }}>{a.keyStatusMessage}</div>
                    )}
                    {a.lastCheckAt && (
                      <div
                        style={{
                          fontSize: 11,
                          color: a.lastCheckOk ? 'var(--text-3)' : 'var(--red)',
                        }}
                      >
                        Last test {timeAgo(a.lastCheckAt)}: {a.lastCheckOk ? 'OK' : 'failed'}
                        {a.lastCheckMessage ? ` — ${a.lastCheckMessage}` : ''}
                      </div>
                    )}
                  </div>
                  <KeyStatusPill integration={a} />
                  {a.status === 'active' ? (
                    <StatusPill status="active">Active</StatusPill>
                  ) : (
                    <StatusPill status="inactive">Disabled</StatusPill>
                  )}
                  <IconButton
                    icon="edit"
                    iconSize={13}
                    size="sm"
                    label="Edit"
                    className="row-action"
                    onClick={() => beginEdit(a)}
                  />
                  <IconButton
                    icon="trash"
                    iconSize={13}
                    size="sm"
                    label="Remove"
                    className="row-action danger"
                    onClick={() => removeAccount(a)}
                  />
                </div>
              ))}
            </div>
          )}

          {formOpen ? (
            <>
              <Field
                label="Name"
                required
                hint={`A label to recognize this connection later, e.g. 'Marketing ${kind.name} account'.`}
              >
                <Input
                  autoFocus
                  value={name}
                  placeholder={`Marketing ${kind.name} account`}
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>

              <Field
                label="API key"
                required={editing === null}
                error={keyError ?? undefined}
                hint={
                  editing ? (
                    'Leave blank to keep the current key; enter a new one to rotate it.'
                  ) : (
                    <>
                      Find or create one at{' '}
                      <a
                        href={kind.apiKeysUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'var(--brand)' }}
                      >
                        {kind.apiKeysUrl.replace(/^https?:\/\//, '')}
                      </a>{' '}
                      ({kind.apiKeysHint}). Stored encrypted at rest.
                    </>
                  )
                }
              >
                <Input
                  type="password"
                  value={apiKey}
                  placeholder={editing ? 'Leave blank to keep current key' : kind.apiKeyPlaceholder}
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    setTestResult(null);
                  }}
                />
              </Field>

              {kind.supportsBaseUrl ? (
                <Field
                  label="API base URL"
                  hint="Ecomail serves per-tenant hosts. Leave the default unless your account uses a different API host."
                >
                  <Input
                    value={baseUrl}
                    placeholder={kind.apiEndpoint}
                    onChange={(e) => setBaseUrl(e.target.value)}
                  />
                </Field>
              ) : (
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  API endpoint: <code>{kind.apiEndpoint}</code>
                </div>
              )}

              {kind.supportsSender && (
                <>
                  <Field
                    label="Sender name"
                    required
                    hint="Applied as the sender name on lists Datahub creates in this account."
                  >
                    <Input
                      value={fromName}
                      placeholder="e.g. Medical Insider"
                      onChange={(e) => setFromName(e.target.value)}
                    />
                  </Field>

                  <Field
                    label="Sender email"
                    required
                    hint="Must be a deliverable address your provider accepts — a placeholder domain is rejected."
                  >
                    <Input
                      type="email"
                      value={fromEmail}
                      placeholder="e.g. newsletter@medicalinsider.cz"
                      onChange={(e) => setFromEmail(e.target.value)}
                    />
                  </Field>

                  <Field
                    label="Reply-to"
                    hint="Optional — defaults to the sender email when left blank."
                  >
                    <Input
                      type="email"
                      value={replyTo}
                      placeholder="e.g. support@medicalinsider.cz"
                      onChange={(e) => setReplyTo(e.target.value)}
                    />
                  </Field>
                </>
              )}

              {error && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 12px',
                    background: 'var(--red-soft)',
                    color: 'var(--red)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 12.5,
                  }}
                >
                  <Icon name="alert" size={14} /> {error}
                </div>
              )}

              {testResult && <TestResultNotice result={testResult} />}
            </>
          ) : (
            <Button
              variant="ghost"
              leftIcon={<Icon name="plus" size={13} />}
              onClick={beginAdd}
            >
              Connect another account
            </Button>
          )}
        </div>
      </div>
      <div className="ds-modal-foot">
        {formOpen && (
          <Button
            variant="secondary"
            loading={testing}
            disabled={!canTest || busy}
            leftIcon={!testing ? <Icon name="link" size={13} /> : undefined}
            title={
              apiKey.trim()
                ? 'Test the key entered above without saving it'
                : 'Test the stored key of this account'
            }
            onClick={() => void runTest()}
          >
            Test connection
          </Button>
        )}
        <span style={{ flex: 1 }} />
        <Button variant="ghost" onClick={cancel}>
          {formOpen ? 'Cancel' : 'Close'}
        </Button>
        {formOpen && (
          <Button
            variant="primary"
            loading={busy}
            leftIcon={!busy ? <Icon name="check" size={13} /> : undefined}
            onClick={() => void submit()}
          >
            {editing ? 'Save' : 'Connect'}
          </Button>
        )}
      </div>
    </ModalRoot>
  );
}

function KeyStatusPill({ integration }: { integration: Integration }) {
  switch (integration.keyStatus ?? 'ok') {
    case 'invalid':
      return <StatusPill status="dnc">Invalid key</StatusPill>;
    case 'undecryptable':
      return <StatusPill status="dnc">Key unreadable</StatusPill>;
    default:
      return <StatusPill status="active">Key OK</StatusPill>;
  }
}

function TestResultNotice({ result }: { result: IntegrationTestResult }) {
  const title = result.ok
    ? result.accountName
      ? `Connected to ${result.accountName}`
      : 'Connection OK'
    : 'Connection failed';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 6,
        padding: '8px 12px',
        background: result.ok ? 'var(--green-soft)' : 'var(--red-soft)',
        color: result.ok ? 'var(--green)' : 'var(--red)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 12.5,
        lineHeight: 1.45,
      }}
    >
      <Icon name={result.ok ? 'check' : 'alert'} size={14} style={{ marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{title}</div>
        {result.message && <div>{result.message}</div>}
        {result.endpoint && (
          <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', opacity: 0.8 }}>
            {result.endpoint}
          </div>
        )}
      </div>
    </div>
  );
}
